import React from "react";
import { useTranslation } from "react-i18next";
import { mediaUrl } from "@/lib/api";
import PhotoLightbox from "@/components/uploads/PhotoLightbox";

/**
 * A row of photo thumbnails that open in the marketplace's own lightbox (T19 F4).
 *
 * Three places draw the same thing: the person's own turn in the thread (the
 * photo is part of what they said), the job draft card (which is born with the
 * photo on it), and the photos waiting above the composer before Send. One
 * component so the three cannot drift — the lesson `JobPhotoGrid` already
 * learned about the job's own photos.
 *
 * Props:
 *   urls       string[]   relative `/api/uploads/...` or absolute
 *   size       number     thumbnail edge in px
 *   align      "start" | "end"   which side of the column (a user turn is "end")
 *   onRemove   (index) => void   when present, each thumbnail can be taken off
 *   busy       boolean    dims the strip while an upload is in flight
 *   testid     string
 */
export default function DanyPhotoStrip({
  urls = [], size = 72, align = "start", onRemove, busy = false, testid = "dany-photos",
}) {
  const { t } = useTranslation();
  const [zoom, setZoom] = React.useState(null);
  if (!urls.length) return null;
  return (
    <>
      <div
        data-testid={testid}
        style={{
          display: "flex", flexWrap: "wrap", gap: "var(--space-2)",
          justifyContent: align === "end" ? "flex-end" : "flex-start",
          opacity: busy ? 0.6 : 1,
        }}
      >
        {urls.map((url, i) => (
          <span key={`${url}-${i}`} style={{ position: "relative", display: "inline-block" }}>
            <button
              type="button"
              onClick={() => setZoom(i)}
              aria-label={t("uploader.expandAlt")}
              data-testid={`${testid}-${i}`}
              style={{
                width: size, height: size, padding: 0, border: "1px solid var(--border-default)",
                borderRadius: "var(--radius-md)", overflow: "hidden", cursor: "zoom-in",
                background: "var(--surface-raised)", display: "block",
              }}
            >
              <img src={mediaUrl(url)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            </button>
            {onRemove && (
              <button
                type="button"
                onClick={() => onRemove(i)}
                aria-label={t("common.remove")}
                data-testid={`${testid}-remove-${i}`}
                style={{
                  position: "absolute", top: -6, right: -6, width: 22, height: 22,
                  borderRadius: "var(--radius-pill)", border: "none", cursor: "pointer",
                  background: "var(--surface-inverse, #1f1a14)", color: "var(--paper, #fff)",
                  fontSize: 14, lineHeight: "22px", textAlign: "center", padding: 0,
                }}
              >
                ×
              </button>
            )}
          </span>
        ))}
      </div>
      <PhotoLightbox urls={urls} index={zoom} onIndex={setZoom} onClose={() => setZoom(null)} />
    </>
  );
}
